import type { OperatorUserEntity } from '@data-delivery/permission-ui-api';
import {
  useCurrentUserInfoQuery,
  useOperatorGroupsQuery,
  useOperatorUsersQuery,
} from '@data-delivery/permission-ui-data-access-state';
import React, { useEffect, useMemo, useState } from 'react';
import { useLogout } from '../hooks/use-logout';

interface CopyFieldProps {
  label: string;
  value?: string;
  isSecret?: boolean;
  reveal?: boolean;
}

const CopyField: React.FC<CopyFieldProps> = ({
  label,
  value,
  isSecret,
  reveal,
}: CopyFieldProps) => {
  const [copied, setCopied] = useState(false);
  const display = value || '';
  const handleCopy = async () => {
    if (!display) {
      return;
    }

    try {
      await navigator.clipboard.writeText(display);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {}
  };

  const masked = useMemo(() => {
    if (!isSecret || reveal || !display) {
      return display;
    }

    if (display.length <= 16) {
      return '•'.repeat(display.length);
    }

    const head = display.slice(0, 6);
    const tail = display.slice(-4);
    return `${head}…${tail}`;
  }, [display, isSecret, reveal]);

  return (
    <div className='flex flex-col gap-1 rounded-md border p-3 bg-background/50'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>{label}</span>
        <button
          type='button'
          onClick={handleCopy}
          className='text-xs underline hover:no-underline disabled:opacity-50'
          disabled={!display}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className='whitespace-pre-wrap break-all text-xs max-h-48 overflow-auto'>
        {masked || '—'}
      </pre>
    </div>
  );
};

export const UserInfoPage: React.FC = () => {
  const { logout, isLoggingOut } = useLogout();
  const [showSecrets, setShowSecrets] = useState(false);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  const {
    data: userInfoData,
    isLoading: loading,
    error,
    refetch: load,
  } = useCurrentUserInfoQuery();

  const claims = userInfoData?.claims ?? undefined;
  const tokens = userInfoData?.tokens ?? {
    accessToken: undefined,
    idToken: undefined,
    accessTokenExpiresAt: undefined,
    idTokenExpiresAt: undefined,
  };

  useEffect(() => {
    const id = setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);

  const claimEntries = claims ? Object.entries(claims) : [];

  const { data: operatorUsersResult } = useOperatorUsersQuery();
  const { data: operatorGroupsResult } = useOperatorGroupsQuery();

  const principalEmail = useMemo(() => {
    if (!claims) {
      return undefined;
    }

    const record = claims;

    const raw = record.email || record.preferred_username;
    return typeof raw === 'string' ? raw : undefined;
  }, [claims]);

  const currentOperatorUser = useMemo(() => {
    if (!principalEmail || !operatorUsersResult) {
      return undefined;
    }

    return operatorUsersResult.find((u: OperatorUserEntity) => u.email === principalEmail);
  }, [principalEmail, operatorUsersResult]);

  const operatorGroups = useMemo(() => operatorGroupsResult ?? [], [operatorGroupsResult]);

  const isRoleObj = (r: unknown): r is { name: string } =>
    Boolean(r) &&
    typeof r === 'object' &&
    'name' in (r as { name: string }) &&
    typeof (r as { name: string }).name === 'string';

  const userBusinessGroupRoles = useMemo(() => {
    const set = new Set<string>();
    const rolesFromUser = currentOperatorUser?.businessGroupRoles;
    rolesFromUser?.forEach(role => {
      if (typeof role === 'string') {
        set.add(role);
      } else if (isRoleObj(role)) {
        set.add(role.name);
      }
    });
    operatorGroups.forEach(g => {
      const groupRoles = g.businessGroupRoles;
      groupRoles?.forEach(role => {
        if (typeof role === 'string') {
          set.add(role);
        } else if (isRoleObj(role)) {
          set.add(role.name);
        }
      });
    });
    return Array.from(set).sort();
  }, [currentOperatorUser, operatorGroups]);

  const operatorStatusDisplay = useMemo(() => {
    const allRoles = [...(currentOperatorUser?.businessGroupRoles ?? [])];
    operatorGroups.forEach(g => {
      allRoles.push(...(g.businessGroupRoles ?? []));
    });

    const isRoleObj = (r: unknown): r is { role: string } =>
      Boolean(r) &&
      typeof r === 'object' &&
      'role' in (r as { role: string }) &&
      typeof (r as { role: string }).role === 'string';

    const hasAdmin = allRoles.some(r => {
      if (typeof r === 'string') {
        return r === 'ADMIN';
      }

      if (isRoleObj(r)) {
        return r.role === 'ADMIN';
      }

      return false;
    });

    if (hasAdmin) {
      return { label: 'Admin', color: 'text-purple-600' };
    }

    const hasOperator = allRoles.some(r => {
      if (typeof r === 'string') {
        return r === 'OPERATOR';
      }

      if (isRoleObj(r)) {
        return r.role === 'OPERATOR';
      }

      return false;
    });

    if (hasOperator) {
      return { label: 'Operator', color: 'text-green-600' };
    }

    if (allRoles.length === 0) {
      return { label: 'None', color: 'text-muted-foreground' };
    }

    const uniqueRoles = Array.from(
      new Set(
        allRoles.map(r => {
          if (typeof r === 'string') {
            return r;
          }

          if (isRoleObj(r)) {
            return r.role;
          }

          return '';
        }),
      ),
    ).filter(Boolean);

    return { label: uniqueRoles.join(', '), color: 'text-muted-foreground' };
  }, [currentOperatorUser, operatorGroups]);

  const copyAllClaims = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(claims ?? {}, null, 2));
    } catch {}
  };

  const formatCountdown = (expiresAt?: number) => {
    if (!expiresAt) {
      return '—';
    }

    const remaining = expiresAt - now;
    if (remaining <= 0) {
      return 'expired';
    }

    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return `${m}m ${s}s`;
  };

  return (
    <div className='flex flex-col gap-6 py-4'>
      <div className='flex items-center justify-between'>
        <h1 className='text-lg font-semibold'>Current User Info</h1>
        <div className='flex gap-2 flex-wrap justify-end'>
          <button
            type='button'
            onClick={() => load()}
            disabled={loading}
            className='rounded-md border px-3 py-1 text-sm hover:bg-accent disabled:opacity-50'
          >
            Refresh
          </button>
          <button
            type='button'
            onClick={() => {
              setShowSecrets(s => !s);
            }}
            className='rounded-md border px-3 py-1 text-sm hover:bg-accent'
          >
            {showSecrets ? 'Hide Tokens' : 'Show Tokens'}
          </button>
          <button
            type='button'
            onClick={() => copyAllClaims()}
            className='rounded-md border px-3 py-1 text-sm hover:bg-accent'
          >
            Copy All Claims JSON
          </button>
          <button
            type='button'
            onClick={logout}
            disabled={isLoggingOut}
            className='rounded-md border px-3 py-1 text-sm hover:bg-destructive/90 disabled:opacity-50'
          >
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </button>
        </div>
      </div>

      {loading && <div className='text-sm text-muted-foreground'>Loading user details…</div>}
      {error && (
        <div className='text-sm text-destructive'>
          Error: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!loading && !error && (
        <>
          <section className='flex flex-col gap-3'>
            <h2 className='text-base font-medium'>Tokens</h2>
            <div className='grid gap-2 md:grid-cols-1'>
              <div className='flex flex-col gap-1'>
                <CopyField
                  label={`Access Token (expires in ${formatCountdown(
                    tokens.accessTokenExpiresAt,
                  )})`}
                  value={tokens.accessToken}
                  isSecret
                  reveal={showSecrets}
                />
              </div>
              <div className='flex flex-col gap-1'>
                <CopyField
                  label={`ID Token (expires in ${formatCountdown(tokens.idTokenExpiresAt)})`}
                  value={tokens.idToken}
                  isSecret
                  reveal={showSecrets}
                />
              </div>
            </div>
          </section>

          <section className='flex flex-col gap-3'>
            <h2 className='text-base font-medium'>Claims</h2>
            {claimEntries.length === 0 && (
              <div className='text-xs text-muted-foreground'>No claims available.</div>
            )}
            <div className='grid gap-2 md:grid-cols-2'>
              {claimEntries.map(([key, val]) => {
                const str = typeof val === 'string' ? val : JSON.stringify(val, null, 2);
                return <CopyField key={key} label={key} value={str} />;
              })}
            </div>
          </section>

          <section className='flex flex-col gap-3'>
            <h2 className='text-base font-medium'>Permission UI Roles</h2>
            {!principalEmail && (
              <div className='text-xs text-muted-foreground'>No principal email claim found.</div>
            )}
            <div className='grid gap-2 md:grid-cols-2'>
              <CopyField
                label='Operator User'
                value={
                  currentOperatorUser
                    ? currentOperatorUser.name || currentOperatorUser.email
                    : 'Not mapped'
                }
              />
              <div className='flex flex-col gap-1 rounded-md border p-3 bg-background/50'>
                <span className='text-sm font-medium'>Operator Status</span>
                <span className={`text-sm font-medium ${operatorStatusDisplay.color}`}>
                  {operatorStatusDisplay.label}
                </span>
              </div>
              <CopyField
                label='Business Group Roles (Merged)'
                value={userBusinessGroupRoles.length ? userBusinessGroupRoles.join(', ') : 'None'}
              />
              <CopyField label='Operator Groups Count' value={String(operatorGroups.length)} />
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default UserInfoPage;
